
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Brain, TrendingUp, Users, Clock, Target, BarChart3 } from "lucide-react";

const InsightsTab = () => {
  const aiMetrics = [
    { name: "Candidate Matching Accuracy", value: 94, trend: "+2.5%" },
    { name: "Resume Screening Efficiency", value: 87, trend: "+1.8%" },
    { name: "Interview Scheduling Success", value: 91, trend: "+3.2%" },
    { name: "Client Satisfaction Score", value: 96, trend: "+0.9%" }
  ];

  const systemInsights = [
    {
      icon: Brain,
      title: "AI Performance Overview",
      description: "Your AI agents have processed 1,247 candidates this month with 94% accuracy.",
      metric: "94%",
      color: "bg-blue-500"
    },
    {
      icon: TrendingUp,
      title: "Recruitment Efficiency",
      description: "Time-to-hire reduced by 35% using AI-powered candidate screening.",
      metric: "35%",
      color: "bg-green-500"
    },
    {
      icon: Users,
      title: "Candidate Pipeline Health",
      description: "Active pipeline contains 342 qualified candidates across all positions.",
      metric: "342",
      color: "bg-purple-500"
    },
    {
      icon: Clock,
      title: "Response Time Optimization",
      description: "Average response time to candidate inquiries improved to 2.3 hours.",
      metric: "2.3h",
      color: "bg-orange-500"
    }
  ];

  return (
    <div className="space-y-6">
      {/* AI Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              AI Performance Metrics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {aiMetrics.map((metric) => (
                <div key={metric.name}>
                  <div className="flex justify-between mb-1">
                    <div className="text-sm font-medium">{metric.name}</div>
                    <div className="text-sm text-green-600 font-medium">
                      {metric.trend}
                    </div>
                  </div>
                  <Progress value={metric.value} className="h-2" />
                  <div className="text-xs text-muted-foreground mt-1">
                    Current: {metric.value}%
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>System Insights</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {systemInsights.slice(0, 2).map((insight) => (
                <div key={insight.title} className="flex items-start space-x-3">
                  <div className={`p-2 rounded-md ${insight.color}`}>
                    <insight.icon className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium">{insight.title}</h3>
                      <Badge variant="secondary">{insight.metric}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {insight.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recruitment Intelligence Dashboard */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            Recruitment Intelligence Dashboard
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {systemInsights.slice(2).map((insight) => (
              <div key={insight.title} className="flex items-start space-x-3 p-4 bg-muted rounded-lg">
                <div className={`p-2 rounded-md ${insight.color}`}>
                  <insight.icon className="h-4 w-4 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium">{insight.title}</h3>
                    <Badge variant="outline">{insight.metric}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {insight.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* AI Recommendations */}
      <Card>
        <CardHeader>
          <CardTitle>AI Recommendations</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="text-sm font-medium text-blue-900 mb-2">
                Optimize Interview Scheduling
              </h3>
              <p className="text-sm text-blue-700 mb-3">
                Consider implementing automated scheduling for initial screenings to improve efficiency by an estimated 25%.
              </p>
              <Button size="sm" variant="outline" className="border-blue-300 text-blue-700">
                Learn More
              </Button>
            </div>
            
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <h3 className="text-sm font-medium text-green-900 mb-2">
                Enhance Candidate Matching
              </h3>
              <p className="text-sm text-green-700 mb-3">
                Your AI matching accuracy could improve by training on recent successful placements.
              </p>
              <Button size="sm" variant="outline" className="border-green-300 text-green-700">
                Start Training
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InsightsTab;
